import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// Separate contexts for state and actions to prevent unnecessary re-renders
const ChatbotStateContext = createContext();
const ChatbotActionsContext = createContext();

export const useChatbotState = () => {
  const context = useContext(ChatbotStateContext);
  if (!context) {
    throw new Error('useChatbotState must be used within ChatbotProvider');
  }
  return context;
};

export const useChatbotActions = () => {
  const context = useContext(ChatbotActionsContext);
  if (!context) {
    throw new Error('useChatbotActions must be used within ChatbotProvider');
  }
  return context;
};

// Legacy hook for backwards compatibility
export const useChatbot = () => {
  const state = useChatbotState();
  const actions = useChatbotActions();
  return { ...state, ...actions };
};

export const ChatbotProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentBook, setCurrentBook] = useState(null);
  const [currentPage, setCurrentPage] = useState({ path: '/', name: 'Home' });
  const [conversationId, setConversationId] = useState(() => {
    // Get or create conversation ID for this session
    const stored = sessionStorage.getItem('chatbot_conversation_id');
    if (stored) return stored;
    const newId = `conv_${Math.random().toString(36).substring(2)}`;
    sessionStorage.setItem('chatbot_conversation_id', newId);
    return newId;
  });

  const openChatbot = useCallback((book = null) => {
    if (book) {
      setCurrentBook(book);
    }
    setIsOpen(true);
  }, []);

  const closeChatbot = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetConversation = useCallback(() => {
    const newId = `conv_${Math.random().toString(36).substring(2)}`;
    setConversationId(newId);
    sessionStorage.setItem('chatbot_conversation_id', newId);
  }, []);

  const updateCurrentBook = useCallback((book) => {
    setCurrentBook(book);
  }, []);

  const updateCurrentPage = useCallback((path, name, context = {}) => {
    setCurrentPage({ path, name, context });
  }, []);

  // State value - changes frequently
  const stateValue = useMemo(() => ({
    isOpen,
    currentBook,
    currentPage,
    conversationId,
  }), [isOpen, currentBook, currentPage, conversationId]);

  // Actions value - never changes (all functions are wrapped in useCallback)
  const actionsValue = useMemo(() => ({
    openChatbot,
    closeChatbot,
    resetConversation,
    updateCurrentBook,
    updateCurrentPage,
  }), [openChatbot, closeChatbot, resetConversation, updateCurrentBook, updateCurrentPage]);

  return (
    <ChatbotStateContext.Provider value={stateValue}>
      <ChatbotActionsContext.Provider value={actionsValue}>
        {children}
      </ChatbotActionsContext.Provider>
    </ChatbotStateContext.Provider>
  );
};
